import type { BrowseCard } from '@photos/shared';
import { useBrowse, type BrowseKind } from '../api/hooks';
import { Hero } from '../components/Hero';
import { TagChip } from '../components/TagChip';
import { fmtCount, fmtRangeIso, fmtYearRange } from '../lib/format';
import { useLibraryState } from '../state/libraryParams';

const TITLE: Record<BrowseKind, string> = {
  years: 'Browse by year',
  regions: 'Browse by region',
  events: 'Events',
  churches: 'Browse by church',
  groups: 'Browse by group',
};
const NOUN: Record<BrowseKind, string> = {
  years: 'year tags',
  regions: 'region tags',
  events: 'events',
  churches: 'church tags',
  groups: 'groups',
};

function Mosaic({ covers }: { covers: BrowseCard['covers'] }) {
  return (
    <div className="mosaic">
      {covers.slice(0, 3).map((c) => (
        <img key={c.id} alt="" loading="lazy" src={c.thumbUrl} />
      ))}
    </div>
  );
}

/** Years / Regions / Events / Churches / Groups overview cards (prototype `renderCards` / `renderEvents`). */
export function BrowseView({ kind }: { kind: BrowseKind }) {
  const { state, go } = useLibraryState();
  const browse = useBrowse(kind, state.source);
  const cards = browse.data?.cards ?? [];
  const total = browse.data?.total ?? 0;
  const sub = browse.data ? [`${fmtCount(cards.length)} ${NOUN[kind]}`, `${fmtCount(total)} photos`] : [];

  // Groups: fellowship groups first, then service groups, each under its own heading.
  const groupSections =
    kind === 'groups'
      ? [
          {
            label: 'Fellowship',
            cards: cards.filter((c) => c.category === 'fellowship'),
          },
          {
            label: 'Service',
            cards: cards.filter((c) => c.category === 'service'),
          },
        ].filter((sec) => sec.cards.length)
      : [];

  const renderCard = (c: BrowseCard) => (
    <button type="button" className="card" key={c.tags.join('|')} onClick={() => go({ tab: 'all', tags: c.tags })}>
      <Mosaic covers={c.covers} />
      <div className="body">
        <div className="t">{c.label}</div>
        <div className="s">
          {kind === 'events' ? (
            <>
              <span>{fmtRangeIso(c.firstTakenAt, c.lastTakenAt)}</span>
              <span>{fmtCount(c.count)} photos</span>
              {c.picks > 0 && <span style={{ color: 'var(--gold)' }}>★ {c.picks} picks</span>}
            </>
          ) : (
            <>
              <span>{fmtCount(c.count)} photos</span>
              <span>{kind === 'years' ? fmtRangeIso(c.firstTakenAt, c.lastTakenAt) : fmtYearRange(c.firstTakenAt, c.lastTakenAt)}</span>
            </>
          )}
        </div>
        {kind === 'events' && (
          <div className="tags">
            {c.tags.map((t) => (
              <TagChip key={t} tag={t} />
            ))}
          </div>
        )}
      </div>
    </button>
  );

  return (
    <>
      <Hero title={TITLE[kind]} sub={sub} coverUrl={cards[0]?.covers[0]?.thumbUrl} layoutKey={`${kind}|${sub.join('|')}`} />
      {browse.isError ? (
        <div className="empty">
          <h3>Couldn’t load {TITLE[kind].toLowerCase()}</h3>
          {browse.error.message}
        </div>
      ) : !browse.data ? (
        <div className="sentinel">
          <div className="spinner" />
        </div>
      ) : !cards.length ? (
        <div className="empty">
          <h3>Nothing here yet</h3>
          Tagged photos will show up here.
        </div>
      ) : groupSections.length ? (
        groupSections.map((sec) => (
          <section key={sec.label}>
            <h2 className="subhead page">{sec.label}</h2>
            <div className="cards">{sec.cards.map(renderCard)}</div>
          </section>
        ))
      ) : (
        <div className="cards">{cards.map(renderCard)}</div>
      )}
    </>
  );
}
